import React, { useRef, useState } from 'react';
import { Film, Play, Pause, Scissors, X, AlertCircle, Loader2 } from 'lucide-react';
import { useFileUrl } from '../../hooks/useFileUrl';
import { DEFAULT_REFERENCE_LIMITS, clampClipRange, defaultClipRange, formatClock, type ClipRange, type ReferenceMediaLimits } from '../../utils/referenceClips';
import { ensureVideoDuration } from '../../utils/videoTrim';
import { chipButtonClass } from './composer/Composer';

export interface ReferenceClipInput {
  /** Connection id, used to disconnect. */
  id: string;
  /** Source node id, used to remember the selection. */
  sourceId: string;
  url: string;
  label: string;
}

interface ReferenceClipEditorProps {
  clip: ReferenceClipInput;
  range?: ClipRange;
  durationSec?: number;
  /** The new video continues from the end of the selection (extend) rather than using it as a style reference. */
  continues: boolean;
  /** How long a selection the model takes (Seedance 2.5: 30 s, 2.0: 15 s). */
  limits?: ReferenceMediaLimits;
  /** How the prompt names this clip (@Video1…), and a way to insert that into the prompt. */
  tag?: string;
  onInsertTag?: (tag: string) => void;
  onRangeChange: (sourceId: string, range: ClipRange) => void;
  onDurationKnown: (sourceId: string, seconds: number) => void;
  onDisconnect?: (connectionId: string) => void;
  userId?: string;
  workspaceName?: string;
  ownerUserId?: string;
  disabled?: boolean;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

/** A small chip to press, for a row 32px high: Trim in the node's tone, the two ready-made selections plain. */
const TRIM_CHIP = `${chipButtonClass({ pressed: true })} ns-chip-sm`;
const PRESET_CHIP = `${chipButtonClass()} ns-chip-sm`;

/**
 * One connected video with a trim control: a preview, a timeline with start and
 * end handles, and a readout of what will be sent. Dragging a handle scrubs the
 * preview to that point, so the cut can be placed exactly.
 */
export const ReferenceClipEditor: React.FC<ReferenceClipEditorProps> = ({
  clip, range, durationSec, continues, limits = DEFAULT_REFERENCE_LIMITS, tag, onInsertTag, onRangeChange, onDurationKnown, onDisconnect, userId, workspaceName, ownerUserId, disabled = false,
}) => {
  const maxSec = limits.clipMaxSec;
  const { url, isLoading, error } = useFileUrl(userId, clip.url, workspaceName, ownerUserId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'start' | 'end' | null>(null);
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const duration = durationSec ?? 0;
  const selection = range ?? (duration > 0 ? defaultClipRange(duration, limits) : undefined);
  const percent = (seconds: number) => `${duration > 0 ? (seconds / duration) * 100 : 0}%`;

  const scrubTo = (seconds: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    if (!video.paused) video.pause();
    setPreviewing(false);
    video.currentTime = seconds;
  };

  const moveEdge = (edge: 'start' | 'end', seconds: number) => {
    if (duration <= 0 || !selection) return;
    const next = clampClipRange(
      { startSec: edge === 'start' ? seconds : selection.startSec, endSec: edge === 'end' ? seconds : selection.endSec },
      duration,
      edge,
      limits,
    );
    onRangeChange(clip.sourceId, next);
    scrubTo(edge === 'start' ? next.startSec : next.endSec);
  };

  const timeFromPointer = (e: React.PointerEvent): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    return Math.min(duration, Math.max(0, ((e.clientX - rect.left) / rect.width) * duration));
  };

  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || duration <= 0 || !selection) return;
    const at = timeFromPointer(e);
    const edge: 'start' | 'end' = Math.abs(at - selection.startSec) < Math.abs(at - selection.endSec) ? 'start' : 'end';
    draggingRef.current = edge;
    e.currentTarget.setPointerCapture(e.pointerId);
    moveEdge(edge, at);
  };

  const onTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) moveEdge(draggingRef.current, timeFromPointer(e));
  };

  const onTrackPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleKey = (edge: 'start' | 'end') => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || !selection) return;
    const step = e.shiftKey ? 2 : 0.5;
    const current = edge === 'start' ? selection.startSec : selection.endSec;
    if (e.key === 'ArrowLeft') { e.preventDefault(); moveEdge(edge, current - step); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); moveEdge(edge, current + step); }
    else if (e.key === 'Home') { e.preventDefault(); moveEdge(edge, 0); }
    else if (e.key === 'End') { e.preventDefault(); moveEdge(edge, duration); }
  };

  const togglePreview = () => {
    const video = videoRef.current;
    if (!video || !selection) return;
    if (previewing) {
      video.pause();
      setPreviewing(false);
      return;
    }
    video.currentTime = selection.startSec;
    void video.play().then(() => setPreviewing(true)).catch(() => setPreviewing(false));
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !previewing || !selection) return;
    if (video.currentTime >= selection.endSec) {
      video.pause();
      video.currentTime = selection.endSec;
      setPreviewing(false);
    }
  };

  const length = selection ? selection.endSec - selection.startSec : 0;

  return (
    <div className="rounded-lg bg-cream-100 ring-1 ring-cream-300 text-2xs text-ink-soft">
      <div className="flex items-center gap-2 h-8 pl-2.5 pr-1">
        <Film size={12} className="text-[color:var(--tone-deep)] shrink-0" aria-hidden="true" />
        <span className="truncate flex-1 text-ink" title={clip.label}>{clip.label}</span>
        {tag && (
          <button
            type="button"
            onClick={() => onInsertTag?.(tag)}
            disabled={disabled || !onInsertTag}
            className="no-drag shrink-0 px-1.5 rounded-full bg-white ring-1 ring-cream-300 font-semibold text-ink hover:ring-lavender"
            title={`Insert ${tag} into the prompt`}
            data-ref-tag
          >
            {tag}
          </button>
        )}
        {selection && (
          <span className="text-ink-faint shrink-0 tabular-nums">{formatClock(selection.startSec)} to {formatClock(selection.endSec)}</span>
        )}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={TRIM_CHIP}
          aria-expanded={open}
          disabled={disabled}
        >
          <Scissors size={12} /> {open ? 'Done' : 'Trim'}
        </button>
        {onDisconnect && (
          <button type="button" onClick={() => onDisconnect(clip.id)} className="no-drag ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" aria-label={`Disconnect ${clip.label}`} disabled={disabled}>
            <X />
          </button>
        )}
      </div>

      {open && (
        <div className="px-2 pb-2 flex flex-col gap-2">
          {isLoading ? (
            <div className="flex items-center gap-1 text-ink-faint"><Loader2 size={12} className="animate-spin" /> Loading video</div>
          ) : error || !url ? (
            <div className="flex items-center gap-1 text-warn-deep"><AlertCircle size={12} /> {error || 'Video unavailable'}</div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={url}
                muted
                playsInline
                preload="metadata"
                className="no-drag w-full max-h-32 rounded bg-ink object-contain"
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  void ensureVideoDuration(video).then((d) => {
                    if (d <= 0) return;
                    onDurationKnown(clip.sourceId, round1(d));
                    const initial = range ?? defaultClipRange(d, limits);
                    video.currentTime = initial.endSec;
                  });
                }}
                onTimeUpdate={onTimeUpdate}
                onPause={() => setPreviewing(false)}
                onEnded={() => setPreviewing(false)}
              />

              <div
                ref={trackRef}
                className="no-drag relative h-7 rounded bg-cream-300 cursor-pointer touch-none select-none"
                onPointerDown={onTrackPointerDown}
                onPointerMove={onTrackPointerMove}
                onPointerUp={onTrackPointerUp}
                onPointerCancel={onTrackPointerUp}
              >
                {selection && duration > 0 && (
                  <>
                    {/* (The handles are bare buttons, as tall as the track: `top-0` in their classes is also what keeps
                        the app's 40px least height for a button without a class of the system off them.) */}
                    <div className="absolute inset-y-0 bg-[color:var(--tone-line)] rounded" style={{ left: percent(selection.startSec), width: percent(length) }} aria-hidden="true" />
                    <button
                      type="button"
                      role="slider"
                      aria-label="Selection start"
                      aria-valuemin={0}
                      aria-valuemax={duration}
                      aria-valuenow={selection.startSec}
                      aria-valuetext={formatClock(selection.startSec)}
                      className="absolute top-0 h-full w-2.5 -ml-1.5 rounded bg-ink cursor-ew-resize"
                      style={{ left: percent(selection.startSec) }}
                      onKeyDown={handleKey('start')}
                      disabled={disabled}
                    />
                    <button
                      type="button"
                      role="slider"
                      aria-label="Selection end"
                      aria-valuemin={0}
                      aria-valuemax={duration}
                      aria-valuenow={selection.endSec}
                      aria-valuetext={formatClock(selection.endSec)}
                      className="absolute top-0 h-full w-2.5 -ml-1 rounded bg-flame cursor-ew-resize"
                      style={{ left: percent(selection.endSec) }}
                      onKeyDown={handleKey('end')}
                      disabled={disabled}
                    />
                  </>
                )}
              </div>

              {selection && duration > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <button
                    type="button"
                    onClick={togglePreview}
                    className="no-drag ns-icon-btn ns-icon-btn-xs ns-icon-btn-tone"
                    aria-label={previewing ? 'Pause preview' : 'Preview selection'}
                    disabled={disabled}
                  >
                    {previewing ? <Pause /> : <Play className="ml-px" />}
                  </button>
                  <span className="tabular-nums">
                    Keep <b className="font-semibold text-ink">{formatClock(selection.startSec)}</b> to <b className="font-semibold text-ink">{formatClock(selection.endSec)}</b> · {formatClock(length)} of {formatClock(duration)}
                  </span>
                  <span className="ml-auto flex gap-1">
                    {duration <= maxSec + 0.05 && (
                      <button type="button" onClick={() => onRangeChange(clip.sourceId, { startSec: 0, endSec: round1(duration) })} className={PRESET_CHIP} disabled={disabled}>
                        Whole clip
                      </button>
                    )}
                    {duration > maxSec + 0.05 && (
                      <button type="button" onClick={() => onRangeChange(clip.sourceId, defaultClipRange(duration, limits))} className={PRESET_CHIP} disabled={disabled}>
                        Last {maxSec}s
                      </button>
                    )}
                  </span>
                </div>
              )}

              <p className="ns-t-meta">
                {continues
                  ? 'The new video continues from the end of this selection. Drag the orange handle to the moment it should pick up from.'
                  : 'Only the selection is sent. Drag the handles to choose the part the model should look at.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferenceClipEditor;
